import { type FC } from "react";
import { MenuItem, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ProductCurrencyType,
  ProductTypeType,
} from "@/types/graphql/partner.generated";
import { usePaymentProviderAvailability } from "@/partner/entities/platforms";

interface CurrencyFieldProps {
  value: ProductCurrencyType;
  onChange: (value: ProductCurrencyType) => void;
  label?: string;
  required?: boolean;
  /** Тип продукта, к которому относится цена — при SUBSCRIPTION
   * предлагаются только валюты провайдеров с продлением (Stripe, CCBill):
   * Paynet и PayPal карту для продления не сохраняют (см. backend
   * ProviderAvailabilityService.providersForPrice). */
  productType?: ProductTypeType;
}

const ALL_CURRENCY_OPTIONS = [
  { value: ProductCurrencyType.UsdType, key: "products.currency.usd" },
  { value: ProductCurrencyType.EurType, key: "products.currency.eur" },
  { value: ProductCurrencyType.MdlType, key: "products.currency.mdl" },
  { value: ProductCurrencyType.GbpType, key: "products.currency.gbp" },
  { value: ProductCurrencyType.CadType, key: "products.currency.cad" },
  { value: ProductCurrencyType.PlnType, key: "products.currency.pln" },
];

/**
 * Селект валюты цены (референс: partner/components/CurrencyField). Список
 * сужен до валют, реально доступных платформе (usePaymentProviderAvailability
 * — вторая линия обороны, зеркалит backend ProviderAvailabilityService, не
 * источник авторизации). Уже выбранная валюта всегда остаётся в списке —
 * иначе редактирование ранее созданной цены после отключения провайдера
 * осталось бы без видимого значения в селекте.
 */
export const CurrencyField: FC<CurrencyFieldProps> = ({
  value,
  onChange,
  label,
  required,
  productType,
}) => {
  const { t } = useTranslation();
  const { availableCurrencies, subscriptionCurrencies } =
    usePaymentProviderAvailability();
  const isSubscription = productType === ProductTypeType.SubscriptionType;

  const options = ALL_CURRENCY_OPTIONS.filter((option) => {
    if (option.value === value) return true;
    if (isSubscription && option.value === ProductCurrencyType.MdlType) {
      return false;
    }
    if (
      isSubscription &&
      availableCurrencies.length > 0 &&
      !subscriptionCurrencies.includes(option.value)
    ) {
      return false;
    }
    // Пока не подключён ни один провайдер (или данные ещё не загрузились),
    // availableCurrencies пуст — показываем весь список, а не пустой селект.
    if (
      availableCurrencies.length > 0 &&
      !availableCurrencies.includes(option.value)
    ) {
      return false;
    }
    return true;
  }).map((option) => ({ value: option.value, label: t(option.key) }));

  return (
    <TextField
      select
      required={required}
      label={label}
      fullWidth
      variant="outlined"
      value={value}
      onChange={(event) => onChange(event.target.value as ProductCurrencyType)}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};
